import { CommonModule, DatePipe, isPlatformBrowser } from '@angular/common';
import { NgModule, PLATFORM_ID } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Route, RouterModule } from '@angular/router';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPaginationModule } from 'ngx-pagination';
import { SwiperModule } from 'swiper/angular';

import { DynamicPageComponent } from '~/attendee/components/pages/dynamic-page/dynamic-page.component';
import { OfferPageComponent } from '~/attendee/components/pages/offer-page/offer-page.component';
import { CmsOfferSectionComponent } from '~/attendee/components/shared/section/offer/offer-section.component';
import { NavigatorService } from '~/attendee/service/navigator.service';
import { PaginatedSearchService } from '~/attendee/service/paginated-search.service';
import { AuthModule, SESSION_STORAGE } from '~/auth/auth.module';
import { AuthGuard } from '~/auth/guards/auth.guard';
import { ComponentModule } from '~/component/component.module';

import { CoreModule } from '~/core/core.module';
import { Activity } from '~/core/model/activity.model';
import { DisplayAgeRangePipe } from '~/core/pipes/display-age-range.pipe';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { LocalizedDatePipe } from '~/core/pipes/localized-date.pipe';
import { DummyStorageService } from '~/core/services/storage/dummy-storage.service';
import { MessagingModule } from '~/messaging/messaging.module';
import { ParamethorModule } from '~/paramethor/paramethor.module';

import { AttendeeActivityDatesComponent } from './components/pages/activity-dates/activity-dates.component';
import { AttendeeActivityDetailComponent } from './components/pages/activity-detail/activity-detail.component';
import { AttendeeBookingsComponent } from './components/pages/bookings/bookings.component';
import { AttendeeBookingsListComponent } from './components/pages/bookings/list/list.component';
import { AttendeeHomeFiltersDesktopComponent } from './components/pages/home/filters/filters-desktop/filters-desktop.component';
import { AttendeeHomeFiltersMobileComponent } from './components/pages/home/filters/filters-mobile/filters-mobile.component';
import { AttendeeHomeFiltersComponent } from './components/pages/home/filters/filters.component';
import { AttendeeHomeComponent } from './components/pages/home/home/home.component';
import { AttendeeHomepageComponent } from './components/pages/home/homepage/homepage.component';
import { AttendeeJumbotronActivitiesComponent } from './components/pages/home/jumbotrons/activities/activities.component';
import { AttendeeInboxComponent } from './components/pages/inbox/inbox.component';
import { AttendeeOrganizerActivitiesComponent } from './components/pages/organizer-page/organizer-activities/organizer-activities.component';
import { AttendeeOrganizerDescriptionComponent } from './components/pages/organizer-page/organizer-description/organizer-description.component';
import { AttendeeOrganizerPageComponent } from './components/pages/organizer-page/organizer-page.component';
import { AttendeeOrganizerReviewsComponent } from './components/pages/organizer-page/organizer-reviews/organizer-reviews.component';
import { AgeFilterComponent } from './components/pages/result-page/filters/age/age-filter.component';
import { PriceFilterComponent } from './components/pages/result-page/filters/price/price-filter.component';
import { AttendeeResultPageFiltersComponent } from './components/pages/result-page/filters/search/filters.component';
import { ResultPageComponent } from './components/pages/result-page/result-page.component';
import { ResultPageActivitiesComponent } from './components/pages/result-page/tab/activities/activities.component';
import { ResultPageLivesComponent } from './components/pages/result-page/tab/lives/lives.component';
import { AttendeeReviewsPageComponent } from './components/pages/reviews-page/reviews-page.component';
import { SeoSitemapComponent } from './components/pages/sitemap/sitemap.component';
import { AttendeeActivityCardComponent } from './components/shared/activity-card/activity-card.component';
import { AttendeeActivityCollectionComponent } from './components/shared/activity-card/collection/activity-collection.component';
import { AttendeeActivityOffersComponent } from './components/shared/activity-offers/offers.component';
import { AttendeeActivityTimeslotListComponent } from './components/shared/activity-timeslot-list/activity-timeslot-list.component';
import { AttendeeBannerComponent } from './components/shared/banner/banner.component';
import { AttendeeBookingDetailComponent } from './components/shared/booking-detail/booking-detail.component';
import { AttendeeBookingOffersComponent } from './components/shared/booking-offers/booking-offers.component';
import { AttendeeCardCollectionComponent } from './components/shared/card/card-collection/card-collection.component';
import { AttendeeCardComponent } from './components/shared/card/card.component';
import { AttendeeCategoryComponent } from './components/shared/category/category.component';
import { AttendeeCategoryCollectionComponent } from './components/shared/category/collection/category-collection.component';
import { AttendeeChipsFilterComponent } from './components/shared/chips-filter/chips-filter.component';
import { AttendeeCityAutocompleteComponent } from './components/shared/city-autocomplete/city-autocomplete.component';
import { CmsLinkComponent } from './components/shared/cms-link/cms-link.component';
import { AttendeeDateFilterComponent } from './components/shared/date-filter/date-filter.component';
import { AttendeeDateRangeInputComponent } from './components/shared/date-range-input/date-range-input.component';
import { AttendeeFiltersComponent } from './components/shared/filters/filters.component';
import { AttendeeFooterComponent } from './components/shared/footer/footer.component';
import { AttendeeGlobalReviewComponent } from './components/shared/global-review/global-review.component';
import { AttendeeHeaderComponent } from './components/shared/header/header.component';
import { AttendeeHeaderNavigationComponent } from './components/shared/header/navigation/navigation.component';
import { AttendeeJumbotronComponent } from './components/shared/jumbotron/jumbotron.component';
import { AttendeeLayoutComponent } from './components/shared/layout/default/layout.component';
import { AttendeeDynamicPageLayoutComponent } from './components/shared/layout/dynamic-page/dynamic-page-layout.component';
import { AttendeeLiveCardComponent } from './components/shared/live-card/live-card.component';
import { NotFoundComponent } from './components/shared/not-found/not-found.component';
import { AttendeePaginationComponent } from './components/shared/pagination/pagination.component';
import { CmsActivitiesSectionComponent } from './components/shared/section/activities/activities-section.component';
import { CmsBannerSectionComponent } from './components/shared/section/banner/banner-section.component';
import { CmsCardCollectionSectionComponent } from './components/shared/section/card-collection/card-collection-section.component';
import { CmsDividerSectionComponent } from './components/shared/section/divider/divider-section.component';
import { CmsImageSectionComponent } from './components/shared/section/image/image-section.component';
import { CmsPictureAndTextSectionComponent } from './components/shared/section/picture-and-text/picture-and-text.component';
import { CmsPipedriveWebformComponent } from './components/shared/section/pipedrive-webform/pipedrive-webform.component';
import { CmsSectionComponent } from './components/shared/section/section.component';
import { CmsTextAndBlocksBlockComponent } from './components/shared/section/text-and-blocks/block/block.component';
import { CmsTextAndBlocksSectionComponent } from './components/shared/section/text-and-blocks/text-and-blocks.component';
import { CmsWidgetSectionComponent } from './components/shared/section/widget/widget-section.component';
import { CmsWysiwygSectionComponent } from './components/shared/section/wysiwyg/wysiwyg-section.component';
import { AttendeeSportFilterComponent } from './components/shared/sport-filter/sport-filter.component';
import { AttendeeSportsGroupsAutocompleteComponent } from './components/shared/sports-groups-autocomplete/sports-groups-autocomplete.component';
import { BookingTypeEnum } from './enums/booking-type.enum';
import { ActivityDetailResolver } from './resolvers/activity-detail.resolver';
import { OrganizerResolver } from './resolvers/organizer.resolver';
import { SitemapResolver } from './resolvers/sitemap.resolver';
import { PictureComponent } from './components/shared/picture/picture.component';
import { RedirectGuard } from '~/core/guards/redirect/redirect.guard';
import { ListPaginationComponent } from './components/pages/result-page/pagination/list-pagination.component';
import { RedirectExternalComponent } from './components/shared/redirect-external/redirect-external.component';
import { RedirectExternalResolver } from '~/organizer/shared/resolvers/redirect-external/redirect-external.resolver';
import { ActivityFilterParamsInterface } from './interfaces/activity-filter-params.interface';
import { ResultPageExternalComponent } from '~/attendee/components/pages/result-page/tab/external/external.component';
import { TotalResultsPipe } from '~/core/pipes/total-results.pipe';

const routes: Route[] = [
  {
    path: '',
    component: AttendeeHomeComponent,
  },
  {
    path: 'c/:slug',
    component: OfferPageComponent,
    canActivate: [RedirectGuard],
  },
  {
    path: 'd/:slug',
    component: DynamicPageComponent,
    canActivate: [RedirectGuard],
  },
  {
    path: 'attendee.result-page',
    component: ResultPageComponent,
    children: [
      {
        path: 'attendee.activities',
        component: ResultPageActivitiesComponent,
      },
      {
        path: 'attendee.external-activities',
        component: ResultPageExternalComponent,
      },
      {
        path: 'attendee.live-activities',
        component: ResultPageLivesComponent,
      },
      {
        path: '**',
        redirectTo: 'attendee.activities',
      },
    ],
  },

  {
    path: 'attendee.show-activity/:id',
    component: AttendeeActivityDetailComponent,
    canActivate: [RedirectGuard],
    resolve: {
      activityDetailData: ActivityDetailResolver,
    },
  },
  {
    path: 'attendee.show-activity-dates/:id',
    component: AttendeeActivityDatesComponent,
    resolve: {
      activityDetailData: ActivityDetailResolver,
    },
  },
  {
    path: 'attendee.show-live-activity/:id',
    component: AttendeeActivityDetailComponent,
    canActivate: [RedirectGuard],
    resolve: {
      activityDetailData: ActivityDetailResolver,
    },
  },
  {
    path: 'attendee.show-live-activity-dates/:id',
    component: AttendeeActivityDatesComponent,
    resolve: {
      activityDetailData: ActivityDetailResolver,
    },
  },
  {
    path: 'attendee.bookings',
    component: AttendeeBookingsComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'attendee.waiting',
        component: AttendeeBookingsListComponent,
        data: { type: BookingTypeEnum.WAITING },
      },
      {
        path: 'attendee.upcoming',
        component: AttendeeBookingsListComponent,
        data: { type: BookingTypeEnum.UPCOMING },
      },
      {
        path: 'attendee.past',
        component: AttendeeBookingsListComponent,
        data: { type: BookingTypeEnum.PAST },
      },
      {
        path: 'attendee.canceled',
        component: AttendeeBookingsListComponent,
        data: { type: BookingTypeEnum.CANCELED },
      },
      {
        path: '**',
        redirectTo: 'attendee.upcoming',
      },
    ],
  },
  {
    path: 'attendee.messages.inbox',
    canActivate: [AuthGuard],
    component: AttendeeInboxComponent,
  },
  // redirect old live page to result page
  {
    path: 'attendee.lives',
    redirectTo: 'attendee.result-page/attendee.live-activities',
  },
  {
    path: 'attendee.sitemap',
    component: SeoSitemapComponent,
    resolve: {
      data: SitemapResolver,
    },
  },
  {
    path: 'attendee.sitemap/:id',
    component: SeoSitemapComponent,
    resolve: {
      data: SitemapResolver,
    },
  },
  {
    path: 'attendee.organizer/:id/:name',
    resolve: {
      data: OrganizerResolver,
    },
    component: AttendeeOrganizerPageComponent,
    children: [
      {
        path: '',
        redirectTo: 'attendee.activities',
        pathMatch: 'full',
      },
      {
        path: 'attendee.activities',
        component: AttendeeOrganizerActivitiesComponent,
      },
      {
        path: 'attendee.reviews',
        component: AttendeeOrganizerReviewsComponent,
      },
      {
        path: '**',
        redirectTo: 'attendee.activities',
      },
    ],
  },
  {
    path: 'attendee.show-activity/:id/attendee.reviews',
    component: AttendeeReviewsPageComponent,
  },
  {
    path: 'attendee.show-live-activity/:id/attendee.reviews',
    component: AttendeeReviewsPageComponent,
  },
  {
    path: 'attendee.external-url/:url',
    component: RedirectExternalComponent,
    resolve: {
      externalUrl: RedirectExternalResolver,
    },
  },
];
/**
 * Attendee/Public website part
 */
@NgModule({
  declarations: [
    AttendeeActivityDatesComponent,
    AttendeeActivityDetailComponent,
    AttendeeActivityCardComponent,
    AttendeeActivityCollectionComponent,
    AttendeeCardCollectionComponent,
    AttendeeCardComponent,
    AttendeeBookingsComponent,
    AttendeeBookingDetailComponent,
    AttendeeBookingOffersComponent,
    AttendeeBookingsListComponent,
    AttendeeHomeComponent,
    AttendeeHomepageComponent,
    AttendeeHeaderComponent,
    AttendeeHeaderNavigationComponent,
    AttendeeLayoutComponent,
    AttendeeDynamicPageLayoutComponent,
    AttendeePaginationComponent,
    AttendeeJumbotronComponent,
    AttendeeHomeFiltersComponent,
    AttendeeHomeFiltersMobileComponent,
    AttendeeHomeFiltersDesktopComponent,
    AttendeeResultPageFiltersComponent,
    AttendeeJumbotronActivitiesComponent,
    AttendeeCityAutocompleteComponent,
    AttendeeDateRangeInputComponent,
    AttendeeLiveCardComponent,
    OfferPageComponent,
    ResultPageComponent,
    ResultPageActivitiesComponent,
    ResultPageLivesComponent,
    ResultPageExternalComponent,
    DynamicPageComponent,
    AttendeeSportFilterComponent,
    AttendeeDateFilterComponent,
    AttendeeCategoryComponent,
    AttendeeCategoryCollectionComponent,
    AttendeeBannerComponent,
    AttendeeInboxComponent,
    AttendeeFiltersComponent,
    AttendeeSportsGroupsAutocompleteComponent,
    AttendeeFooterComponent,
    AttendeeActivityTimeslotListComponent,
    AttendeeActivityOffersComponent,
    AttendeeActivityDatesComponent,
    AttendeeOrganizerPageComponent,
    AttendeeOrganizerDescriptionComponent,
    AttendeeOrganizerReviewsComponent,
    AttendeeOrganizerActivitiesComponent,
    AttendeeReviewsPageComponent,
    AttendeeGlobalReviewComponent,
    PictureComponent,
    ListPaginationComponent,

    // cms
    CmsSectionComponent,
    CmsBannerSectionComponent,
    CmsWysiwygSectionComponent,
    CmsActivitiesSectionComponent,
    CmsCardCollectionSectionComponent,
    CmsWidgetSectionComponent,
    CmsDividerSectionComponent,
    CmsImageSectionComponent,
    CmsOfferSectionComponent,
    CmsPictureAndTextSectionComponent,
    CmsTextAndBlocksSectionComponent,
    CmsTextAndBlocksBlockComponent,
    CmsLinkComponent,
    CmsPipedriveWebformComponent,

    // Result page
    AgeFilterComponent,
    PriceFilterComponent,
    AttendeeChipsFilterComponent,

    // Errors
    NotFoundComponent,

    // SEO
    SeoSitemapComponent,
  ],
  imports: [
    CommonModule,
    CoreModule,
    ParamethorModule,
    RouterModule.forChild(routes),
    LocalizeRouterModule.forChild(routes),
    TranslateModule.forChild(),
    ComponentModule,
    AuthModule,
    ReactiveFormsModule,
    NgxPaginationModule,
    SwiperModule,
    MessagingModule,
  ],
  providers: [
    LocalizedDatePipe,
    DatePipe,
    DisplayPricePipe,
    TotalResultsPipe,
    DisplayAgeRangePipe,
    NavigatorService,

    {
      provide: 'ActivitySearchService',
      useFactory: (
        searchService: PaginatedSearchService<
          Activity,
          ActivityFilterParamsInterface
        >
      ) => {
        searchService.path = 'activities';
        searchService.model = Activity;
        return searchService;
      },
      deps: [PaginatedSearchService],
    },
    {
      provide: SESSION_STORAGE,
      useFactory: (platformId: string) =>
        isPlatformBrowser(platformId)
          ? sessionStorage
          : new DummyStorageService(),
      deps: [PLATFORM_ID],
    },
    {
      provide: 'Window',
      useValue: {
        scrollY: 0,
      },
    },
  ],
  exports: [AttendeeActivityCardComponent, AttendeeHeaderComponent],
})
export class AttendeeModule {}
